import { Flex, Spin } from 'antd';
import { Navigate } from 'react-router-dom';
import { useListMembershipsQuery } from '@/entities/user';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/shared/config/routes';

/** `/` — sends the signed-in user on based on the current store's membership: APPROVED →
 *  /admin, no membership/no store → /create-store, any other status → /no-access. Not wrapped in
 *  `RequireAuth` (it has to decide *where* to send an authenticated user, including away from
 *  `/`), so it must check `auth.status` itself before querying memberships — skipping that check
 *  once caused an infinite loop for a signed-out visit: the query 401s, the failed reauth's
 *  refresh attempt clears the session, whose listener resets the RTK Query cache, which re-fires
 *  this same query — see baseApi.ts's retry fix for the other half of that bug. */
export function RootRedirect() {
  const status = useAppSelector((state) => state.auth.status);
  const currentStoreId = useAppSelector((state) => state.store.currentStoreId);
  const { data: memberships, isLoading } = useListMembershipsQuery(undefined, {
    skip: status !== 'authenticated',
  });

  if (status === 'unauthenticated') {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (status === 'idle' || isLoading || !memberships) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  const membership = currentStoreId
    ? memberships.find((m) => m.storeId === currentStoreId)
    : undefined;

  if (!membership) {
    return <Navigate to={ROUTES.createStore} replace />;
  }
  if (membership.status !== 'APPROVED') {
    return <Navigate to={ROUTES.noAccess} replace />;
  }

  return <Navigate to={ROUTES.admin.root} replace />;
}
